import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  const count = items.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) {
    return (
      <section className="relative h-[420px] md:h-[600px] flex items-center justify-center bg-background-200">
        <div className="text-center">
          <i className="ri-movie-2-line text-5xl text-foreground-400"></i>
          <p className="mt-4 text-foreground-600">暂无推荐电影，敬请期待</p>
        </div>
      </section>
    );
  }

  const current = items[index];

  return (
    <section className="relative w-full h-[560px] md:h-[720px] overflow-hidden bg-foreground-950">
      {items.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={m.backdrop}
            alt={`${m.title} 电影剧照`}
            title={`${m.title} - 爱情电影推荐`}
            className="w-full h-full object-top object-cover"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/45 via-foreground-950/30 to-foreground-950/55"></div>

      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-4 md:px-10">
        <h1 className="font-heading text-background-100 text-xs md:text-sm tracking-[0.3em] mb-4">
          高清电影介绍与影评聚合平台
        </h1>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-xs md:text-sm font-label mb-4">
          <i className="ri-fire-line"></i>
          本周焦点 · 编辑推荐
        </span>
        <h2 className="font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl tracking-wide max-w-4xl">
          {current.title}
        </h2>
        <p className="mt-4 text-accent-300 font-heading text-base md:text-xl max-w-2xl">
          {current.tagline}
        </p>
        <p className="mt-4 text-background-200 text-sm md:text-base max-w-2xl leading-relaxed line-clamp-3">
          {current.summary}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs md:text-sm text-background-100">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-400"></i>
            {current.rating.toFixed(1)}
          </span>
          <span className="w-1 h-1 rounded-full bg-background-300"></span>
          <span>{current.year}</span>
          <span className="w-1 h-1 rounded-full bg-background-300"></span>
          <span>{current.region}</span>
          <span className="w-1 h-1 rounded-full bg-background-300"></span>
          <span>{current.genres.join(" · ")}</span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
          <Link
            to={`/detail/${current.id}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line text-lg"></i>
            查看电影详情
          </Link>
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-grid-line text-lg"></i>
            浏览全部电影
          </Link>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 flex items-center justify-center cursor-pointer transition-colors"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 flex items-center justify-center cursor-pointer transition-colors"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {items.map((m, i) => (
          <button
            key={m.id}
            type="button"
            aria-label={`切换到 ${m.title}`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-primary-400" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}